import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
// NOT USED since the total has its own size (COLLECTOR_FONT_SIZE):
// import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";
import { createCoinLabel, setCoinLabel } from "../../../Games/HoldAndWin/coinLabel";

/**
 * HoldAndWinCollectorController — the End-of-Feature Collector (GDD "End of
 * Hold & Win Feature (Collector)").
 *
 * The animator's `hold_and_win_collector` spine, put in the SAME container as
 * the respin counter, so it appears exactly where the counter was — the
 * counter "turns into" it (counter_transform_N, then this one's in).
 *
 *   collector_in        appears
 *   collector_idle      waits / between arrivals
 *   collector_hit       a value arrives
 *   collector_final_hit the last one — the total is final
 *   collector_out       leaves
 *
 * Its total is on `txt_slot_collector`. Never pixel-rounded: the spine scales
 * it (no rounded coin numbers).
 */

const SKELETON = "hold_and_win_collector-skeleton";
const ATLAS = "hold_and_win_collector-atlas";
const TEXT_SLOT = "txt_slot_collector";

// ── The total's size: 85% of the box's dark window ──
// Measured, not guessed (2026-09-29):
//   the window   the box art `hnw_collector` is 599 x 167 rig units; its dark
//                window, from the atlas pixels, is 521 x 110, centred on the
//                slot (within 1 unit)
//   the text     this label draws ~0.925 x its font size tall, stroke and
//                drop shadow included (measured on screen at font 126: ~116
//                units — which overflowed the 110-unit window)
// 85% of the window: 0.85 x 110 = 93.5 units tall -> font 93.5 / 0.925 ≈ 101;
// width held to 0.85 x 521 ≈ 443. Vladimir: "it can take like 85% of it".
const COLLECTOR_WINDOW = { WIDTH: 521, HEIGHT: 110 };
// 0.85 read a bit big (Vladimir, 2026-09-29): one step down.
// const COLLECTOR_TEXT_FILL = 0.85;
const COLLECTOR_TEXT_FILL = 0.8;
// The coin style's full height per font size — Noto Sans ExtraBold digits
// 0.714 (cap height) + outline 0.138 + the solid lip 0.069 (coin_value_bw).
// Was 0.925 with the old Troika style and its deeper orange shadow.
// const TEXT_HEIGHT_PER_FONT = 0.925;
const TEXT_HEIGHT_PER_FONT = 0.921;
const COLLECTOR_FONT_SIZE = Math.round(
  (COLLECTOR_WINDOW.HEIGHT * COLLECTOR_TEXT_FILL) / TEXT_HEIGHT_PER_FONT,
);
const COLLECTOR_MAX_WIDTH = Math.round(COLLECTOR_WINDOW.WIDTH * COLLECTOR_TEXT_FILL);
// The whole collector's size against the reel box — the box, its glow and
// its total together (the total keeps its fill of the window). 1.0 = as the
// animator authored it, which read too big next to the reels (Vladimir,
// 2026-09-30: "id make it 90% in size").
// const COLLECTOR_SCALE = 0.9;
// Still too big (Vladimir, 2026-09-30): 15% less again — 0.9 × 0.85.
// const COLLECTOR_SCALE = 0.765;
// And 5% less (Vladimir, 2026-09-30): 0.765 × 0.95.
// const COLLECTOR_SCALE = 0.727;
// 5% less once more — "thats perfect" (Vladimir, 2026-09-30): 0.727 × 0.95.
const COLLECTOR_SCALE = 0.69;
// ── Lifted off the counter's spot (Art #2, demo feedback 2026-10-13: "move
// the collector up a little, so the value itself is not behind UI") ── room
// units, up. Only the collector moves; the counter and the room stay put.
// const COLLECTOR_LIFT = 25;
// 25 was too much — "A LITTLE BIT" (Vladimir, 2026-10-13).
// const COLLECTOR_LIFT = 10;
// A few pixels back down (2026-10-14: it covered the value on the chests in
// the bottom row).
// const COLLECTOR_LIFT = 4;
// More (2026-10-14): below the slot's centre now (negative = down).
const COLLECTOR_LIFT = -4;
// How far the collector's box reaches BELOW its slot's origin, in the slot's
// units — the room fits its reel box so this stays above the play button
// (HoldAndWinGameViewController.fitBoxToPlayButton). The box art is 167 tall.
export const COLLECTOR_BOTTOM = Math.ceil((167 * COLLECTOR_SCALE) / 2) - COLLECTOR_LIFT;

class HoldAndWinCollectorController {
  constructor(parentContainer) {
    this.parentContainer = parentContainer;
    this.spine = null;
    this.text = null;
    this.initialize();
  }

  initialize() {
    const assets = PIXI.Assets.cache;
    if (!this.parentContainer || !assets.has(SKELETON) || !assets.has(ATLAS)) return;
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "hold_and_win_collector";
      spine.scale.set(COLLECTOR_SCALE);
      spine.y = -COLLECTOR_LIFT;
      spine.visible = false;
      if (spine.skeleton.findSlot(TEXT_SLOT)) {
        this.text = createCoinLabel();
        spine.addSlotObject(TEXT_SLOT, this.text);
      }
      this.parentContainer.addChild(spine);
      this.spine = spine;
    } catch (error) {
      this.spine = null;
    }
  }

  /** The point trails fly to. */
  get anchor() {
    return this.spine;
  }

  _has(name) {
    return !!this.spine?.skeleton?.data?.findAnimation(name);
  }

  _play(name, loop = false) {
    if (!this.spine || !this._has(name)) return null;
    const entry = this.spine.state.setAnimation(0, name, loop);
    if (!loop && this._has("collector_idle")) {
      this.spine.state.addAnimation(0, "collector_idle", true, 0);
    }
    return entry;
  }

  /** In, starting at `value`. Resolves when `collector_in` has played. */
  show(value = 0) {
    if (!this.spine) return Promise.resolve();
    this.setValue(value);
    this.spine.visible = true;
    this.spine.state.clearTracks();
    this.spine.skeleton.setToSetupPose();
    const entry = this._play("collector_in");
    if (!entry) {
      this._play("collector_idle", true);
      return Promise.resolve();
    }
    // return new Promise((resolve) => {
    //   entry.listener = { complete: () => resolve() };
    // });
    // `end` too (2026-10-08): a clip replaced or cleared (hide, the room
    // leaving) never completes, and the End Collector awaits this.
    return new Promise((resolve) => {
      entry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  setValue(value) {
    if (!this.text) return;
    setCoinLabel(
      this.text,
      value > 0 ? formatToCustomLocale(value) : "",
      COLLECTOR_FONT_SIZE,
      COLLECTOR_MAX_WIDTH,
      // The jackpot plates' gold look, not the plain coin face (Vladimir,
      // 2026-10-01).
      "jackpots",
    );
  }

  /** The size the total is drawn at (rig units) — a flying value uses it. */
  get fontSize() {
    return COLLECTOR_FONT_SIZE;
  }

  /**
   * The total's LIVE vertical scale on screen — everything scaling it (the
   * room, the reel box, the bone, the in/hit clips). A flying value drawn at
   * `fontSize` and scaled to this is exactly the total's size.
   */
  textWorldScale() {
    const wt = this.text?.worldTransform;
    return wt ? Math.hypot(wt.c, wt.d) : 0;
  }

  hit() {
    this._play("collector_hit");
  }

  /**
   * The last one — resolves when `collector_final_hit` has played IN FULL
   * (its own complete callback), so what follows waits for it.
   */
  finalHit() {
    const entry = this._play("collector_final_hit");
    if (!entry) return Promise.resolve();
    // return new Promise((resolve) => {
    //   entry.listener = { complete: () => resolve() };
    // });
    // `end` too (2026-10-08): a clip replaced or cleared (hide, the room
    // leaving) never completes, and the End Collector awaits this.
    return new Promise((resolve) => {
      entry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /** Off the board (the room is leaving). */
  hide() {
    if (!this.spine) return;
    this.spine.state.clearTracks();
    this.spine.visible = false;
  }

  destroy() {
    if (this.spine) {
      this.spine.state?.clearTracks();
      this.spine.removeSlotObjects?.();
      if (this.spine.parent) this.spine.parent.removeChild(this.spine);
      this.spine.destroy();
      this.spine = null;
    }
    this.text?.destroy?.();
    this.text = null;
  }
}

export default HoldAndWinCollectorController;
